import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LayoutGrid, Package, ReceiptText, MessagesSquare, LogOut, ExternalLink, TicketPercent, UserCog } from "lucide-react";
import { api, adminCall, fmt } from "../../api";
import { LogoMark } from "../../components/Logo";
import ProductsPanel from "./ProductsPanel";
import OrdersPanel from "./OrdersPanel";
import MessagesPanel from "./MessagesPanel";
import PromosPanel from "./PromosPanel";
import AccountPanel from "./AccountPanel";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: ReceiptText },
  { id: "promos", label: "Promos", icon: TicketPercent },
  { id: "messages", label: "Messages", icon: MessagesSquare },
  { id: "account", label: "Account", icon: UserCog },
];

const SetupRow = ({ ok, title, text, testid }) => (
  <div className="flex items-start gap-4 rounded-2xl border border-sage/12 bg-white/80 p-5" data-testid={testid}>
    <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${ok ? "bg-sage" : "bg-amber-500"}`} />
    <div>
      <p className="text-sm font-medium text-ink">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-ink/55">{text}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [setup, setSetup] = useState(null);

  useEffect(() => {
    adminCall(() => api.get("/auth/me"))
      .then((r) => setMe(r.data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  useEffect(() => {
    if (!me) return;
    adminCall(() => api.get("/admin/stats")).then((r) => setStats(r.data)).catch(() => {});
    adminCall(() => api.get("/admin/setup")).then((r) => setSetup(r.data)).catch(() => {});
  }, [me, tab]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-cream" data-testid="admin-loading" />;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard-container">
      <header className="sticky top-0 z-40 border-b border-sage/10 bg-cream/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <LogoMark size={32} />
            <div>
              <p className="font-serif text-lg leading-none text-ink">Owner Studio</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">{me.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden items-center gap-1.5 text-xs text-ink/55 transition-colors hover:text-sage sm:flex" data-testid="admin-view-shop">
              View shop <ExternalLink size={12} />
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 rounded-full border border-sage/25 px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-sage transition-colors hover:bg-sage hover:text-cream"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5 pb-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] transition-all ${
                tab === t.id ? "bg-sage text-cream" : "text-ink/55 hover:bg-sage-tint hover:text-sage"
              }`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        {tab === "overview" && (
          <div className="space-y-10" data-testid="admin-overview">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: "Revenue", value: stats ? fmt(stats.revenue_cents) : "—", testid: "stat-revenue" },
                { label: "Orders", value: stats?.orders_count ?? "—", testid: "stat-orders" },
                { label: "Products", value: stats?.products_count ?? "—", testid: "stat-products" },
                { label: "Unread messages", value: stats?.unread_messages ?? "—", testid: "stat-messages" },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-sage/12 bg-white/80 p-6" data-testid={s.testid}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-lavender-deep">{s.label}</p>
                  <p className="mt-2 font-serif text-3xl text-ink">{s.value}</p>
                </div>
              ))}
            </div>
            <div>
              <h2 className="font-serif text-2xl text-ink">Go-live checklist</h2>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <SetupRow
                  testid="setup-payments"
                  ok={setup?.payments_connected}
                  title={setup?.stripe_mode === "test" ? "Payments: Stripe connected (test mode)" : "Payments: live"}
                  text="Checkout works now in test mode. To accept real payments, claim your Stripe account from the Payments tab in Manage — the claim link was also emailed to you. No code changes needed."
                />
                <SetupRow
                  testid="setup-email"
                  ok={setup?.email_configured}
                  title="Receipts: email service active"
                  text="Order receipts and download links are emailed automatically after verified payment. Add a reply-to address later if you'd like customers to reach an inbox."
                />
                <SetupRow
                  testid="setup-storage"
                  ok={setup?.storage_ready}
                  title="Files: private storage ready"
                  text="Paid files are stored in private object storage and are only ever served through per-order download links after verified payment. Preview images are the only public files."
                />
                <SetupRow
                  testid="setup-ads"
                  ok={false}
                  title="Ads: connect your Google Ads tag"
                  text="To run Google Ads, create a conversion action in your Google Ads account, then add your tag ID (AW-…) and purchase conversion label as REACT_APP_GOOGLE_ADS_ID and REACT_APP_GOOGLE_ADS_PURCHASE_LABEL in the frontend secrets. The purchase conversion fires automatically on the confirmation page, only after visitors accept cookies."
                />
                <SetupRow
                  testid="setup-domain"
                  ok={false}
                  title="Domain: connect your custom domain"
                  text="Follow the steps on the Custom Domain Setup page (linked in the footer) once you've deployed: add the domain in Manage → Publishing → Domains, then point your DNS records."
                />
              </div>
            </div>
          </div>
        )}
        {tab === "products" && <ProductsPanel />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "promos" && <PromosPanel />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "account" && <AccountPanel me={me} onMeChanged={() => adminCall(() => api.get("/auth/me")).then((r) => setMe(r.data)).catch(() => {})} />}
      </main>
    </div>
  );
}
